import _ from 'lodash'
import { createSelector } from 'reselect'
import ResidenceTypeCodes from 'shared_src/dictionaries/demographics/ResidenceTypeCodes'
import statuses from 'shared_src/dictionaries/episodeOfCare/statuses'

import { standardDate, FHIR_DATE } from '../lib/Date/Format/index.js'

import * as Compartment from './compartment.js'

const SSN_SYSTEM = 'http://hl7.org/fhir/sid/us-ssn'
const COGNITO_SYSTEM = 'https://aws.amazon.com/cognito/'

const ACCUITY_SYSTEM = 'http://narushealth.com/fhir/patient-accuity'
const DO_NOT_CALL_URL = 'http://narushealth.com/fhir/patient-do-not-call'
const DISCHARGE_REASON_URL =
  'http://narushealth.com/fhir/episodeofcare-caseClosureReason'
const ISSUE_URGENT_URL = 'http://narushealth.com/fhir/extensions/issue-urgent'

export const getPatientId = createSelector(Compartment.patient, patient =>
  _.get(patient, 'id', null))

export const getPatientName = createSelector(Compartment.patient, patient => {
  const firstName = _.get(patient, 'name[0].given[0]')
  const lastName = _.get(patient, 'name[0].family[0]')

  if ((lastName && lastName.length) || (firstName && firstName.length >= 14)) {
    const lastNameSlice =
      lastName.length >= 16 ? lastName.slice(0, 14).concat('...') : lastName
    const firstNameSlice =
      firstName.length >= 16 ? firstName.slice(0, 14).concat('...') : firstName
    return `${lastNameSlice}, ${firstNameSlice}`
  }
  return `${lastName}, ${firstName}`
})

export const getPatientState = createSelector(Compartment.patient, patient =>
  _.get(patient, 'address[0].state', 'N/A'))

export const getPatientDOB = createSelector(Compartment.patient, patient => {
  const dob = _.get(patient, 'birthDate', null)
  if (!dob) {
    return null
  }

  return standardDate(dob, FHIR_DATE)
})

export const getPatientAge = createSelector(Compartment.patient, patient => {
  const dob = _.get(patient, 'birthDate', null)
  if (!dob) {
    return null
  }

  const delta = new Date().getTime() - new Date(dob).getTime()
  return Math.abs(new Date(delta).getUTCFullYear() - 1970)
})

export const getPatientGender = createSelector(Compartment.patient, patient =>
  _.capitalize(_.get(patient, 'gender', null)))

export const getPatientSSN = createSelector(Compartment.patient, patient =>
  _.chain(patient)
    .get('identifier', [])
    .find(i => i.system === SSN_SYSTEM)
    .get('value', null)
    .value())

export const getPatientSSNLast4 = createSelector(getPatientSSN, ssn => {
  if (!ssn) {
    return null
  }
  const result = ssn.replace(/[^0-9]/g, '')
  return `XXX-XX-${result.substring(result.length - 4)}`
})

export const getPatientPAR = createSelector(Compartment.patient, patient =>
  _.chain(patient)
    .get('extension', [])
    .find(e => e.url === ACCUITY_SYSTEM)
    .get('valueString', null)
    .value())

export const getPatientDoNotCall = createSelector(
  Compartment.patient,
  patient =>
    _.chain(patient)
      .get('extension', [])
      .find(e => e.url === DO_NOT_CALL_URL)
      .get('valueBoolean', false)
      .value()
)

export const getPatientResidenceType = createSelector(
  Compartment.unmappedPatient,
  unmappedPatient => {
    const residenceTypeCode = _.get(unmappedPatient, 'residenceType', null)
    if (!residenceTypeCode) {
      return null
    }
    const residenceTypeObj = (ResidenceTypeCodes || []).filter(
      obj => obj.code === residenceTypeCode
    )
    return _.get(residenceTypeObj, '[0].label', null)
  }
)

export const getEnrollmentStatus = createSelector(
  Compartment.episodeOfCare,
  episodeOfCare => {
    if (!episodeOfCare) {
      return null
    }
    const { status } = episodeOfCare
    return _.chain(statuses)
      .filter(s => s.value === status)
      .first()
      .get('label')
      .value()
  }
)

export const getLastStatusUpdateDate = createSelector(
  Compartment.episodeOfCare,
  episodeOfCare => {
    if (!episodeOfCare) {
      return null
    }
    return _.chain(episodeOfCare)
      .get('statusHistory')
      .last()
      .get('period.start')
      .value()
  }
)

export const getDischargeDate = createSelector(
  Compartment.episodeOfCare,
  episodeOfCare => _.get(episodeOfCare, 'period.end', null)
)

export const getDischargeReason = createSelector(
  Compartment.episodeOfCare,
  episodeOfCare =>
    _.chain(episodeOfCare)
      .get('extension', [])
      .find(e => e.url === DISCHARGE_REASON_URL)
      .get('valueString', null)
      .value()
)

export const getPartnerOrganization = createSelector(
  Compartment.episodeOfCare,
  episodeOfCare => {
    const v = _.chain(episodeOfCare)
      .get('managingOrganization', {})
      .get('reference', null)
      .value()
    return v && v.replace(/^#/, '')
  }
)

export const getDateOfDeath = createSelector(
  Compartment.unmappedPatient,
  unmappedPatient => _.get(unmappedPatient, 'deceasedDateTime', null)
)

const hospitalized = {
  reference: 'http://narus.com/Questionnaire/igao-admissions',
  linkId: 'ciweeegk8x51di3aow1g9rjrb8n7yjt'
}

export const isHospitalized = createSelector(
  Compartment.questionnaireResponses,
  questionnaires => {
    const questionnaire = questionnaires?.find(
      q => q.questionnaire?.reference === hospitalized.reference
    )
    const item = questionnaire?.item?.find(
      i => i.linkId === hospitalized.linkId
    )
    const answer = item?.answer?.find(a => a.valueString === 'yes')
    return (answer && true) || false
  }
)

export const isMobileEnrolled = createSelector(Compartment.patient, patient =>
  _.chain(patient)
    .get('identifier', [])
    .find(i => i.system === COGNITO_SYSTEM)
    .get('value', null)
    .value())

export const specPercentage = createSelector(
  Compartment.unmappedPatient,
  unmappedPatient => _.get(unmappedPatient, 'specPercentage', null)
)

export const getClientDivision = createSelector(
  Compartment.unmappedPatient,
  unmappedPatient => _.get(unmappedPatient, 'clientDivision', null)
)

export const getService = createSelector(
  Compartment.unmappedPatient,
  unmappedPatient => _.get(unmappedPatient, 'service', null)
)

export const isUrgent = createSelector(Compartment.carePlans, carePlans => {
  const res = _.find(carePlans, cp =>
    _.chain(cp)
      .get('extension', [])
      .find(e => e.url === ISSUE_URGENT_URL)
      .get('valueBoolean', false)
      .value())

  return !_.isEmpty(res)
})

/** **********************
 ** Allergies
 ** ******************** */
export const hasAllergies = createSelector(
  Compartment.allergies,
  (allergies = []) =>
    !!allergies.find(
      a => a.category === 'medication' || a.criticality === 'high'
    )
)

/** **********************
 ** Choices (Questionnaires)
 ** ******************** */
const supportingDocumentationCode =
  'http://narus.com/Questionnaire/Supporting Documentation'

export const getLastQuestionnaire = createSelector(
  Compartment.questionnaireResponses,
  questionnaires =>
    _.chain(questionnaires)
      .filter(
        q =>
          _.get(q, 'questionnaire.reference') === supportingDocumentationCode
      )
      .sortBy(o => _.get(o, 'meta.lastUpdated'))
      .last()
      .get('item')
      .reduce(
        (acc, item) => ({
          ...acc,
          [_.get(item, 'linkId')]:
            _.get(item, 'answer[0].valueString', 'no') === 'yes'
        }),
        {}
      )
      .value()
)

export const hasDNR = createSelector(
  getLastQuestionnaire,
  questionnaire => !!questionnaire.DNRHave
)

export const hasAdvancedDirective = createSelector(
  getLastQuestionnaire,
  questionnaire => !!questionnaire.ADHave
)

export const hasPOLST = createSelector(
  getLastQuestionnaire,
  questionnaire => !!questionnaire.PPHave
)

/** ******************************
 ** Diagnosis and MedicalHistory
 ** ***************************** */
export const getPatientHealthProgram = createSelector(
  Compartment.unmappedPatient,
  unmappedPatient => _.get(unmappedPatient, 'healthProgram', null)
)

export const getPatientPrimaryConcern = createSelector(
  Compartment.unmappedPatient,
  unmappedPatient => _.get(unmappedPatient, 'primaryConcern', null)
)
